import { Plus, X } from "lucide-react";
import * as React from "react";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";

import {
  containerDefaultsOf,
  type ActionRecord,
} from "@/shared/domain/actions";
import {
  Card,
  Collapsible,
  Field,
  RobotoButton,
  TextInput,
} from "@/shared/foundation/primitives";

import { ComputeFields } from "./ComputeFields";
import styles from "./invocationSpec.module.css";

/**
 * The shape a variable takes, shown as an example. Unlike the compute and
 * container fields, a variable has no setting of the action's own behind it
 * for the placeholder to offer.
 */
const ENV_VAR_EXAMPLE = { key: "LOG_LEVEL", value: "debug" };

/** The container settings, in the order the card shows them. */
const CONTAINER_FIELDS = [
  ["workdir", "Working directory"],
  ["entrypoint", "Entry point"],
  ["command", "Command"],
] as const;

export interface AdvancedSettingsProps {
  /** Where the invocation block lives in the enclosing form. */
  name: string;
  /** The chosen action, for the settings this only overrides. */
  action: ActionRecord | null;
  /** True when the compute fields are shown elsewhere, outside this panel. */
  hideCompute?: boolean;
}

/** Compute, container and timeout overrides on top of the action's own. */
export function AdvancedSettings({
  name,
  action,
  hideCompute = false,
}: AdvancedSettingsProps) {
  const { control } = useFormContext();
  const container = containerDefaultsOf(action);
  const envVars = useFieldArray({
    control,
    name: `${name}.overrides.envVars`,
  });
  const envNameId = React.useId();
  const envValueId = React.useId();
  // Per action, not per run: the runtime reads an unset flag as a download.
  const downloadsInputs =
    action === null ? null : action.requires_downloaded_inputs !== false;

  return (
    <div data-testid="advanced-settings">
      <Collapsible.Root>
        <h4 className={styles.advancedTitle}>
          <Collapsible.Trigger>Advanced settings</Collapsible.Trigger>
        </h4>
        <Collapsible.Panel
          className={styles.advancedPanel}
          data-testid="advanced-settings-body"
        >
          {!hideCompute && (
            <Card.Root size="compact" data-testid="compute-parameters">
              <Card.Header>
                <Card.Title>Compute parameters</Card.Title>
              </Card.Header>
              <Card.Body>
                <ComputeFields name={name} action={action} />
              </Card.Body>
            </Card.Root>
          )}

          <Card.Root size="compact" data-testid="container-parameters">
            <Card.Header>
              <Card.Title>Container parameters</Card.Title>
            </Card.Header>
            <Card.Body>
              <div className={styles.grid}>
                {CONTAINER_FIELDS.map(([key, label]) => (
                  <Controller
                    key={key}
                    control={control}
                    name={`${name}.overrides.${key}`}
                    render={({ field }) => (
                      <Field.Root name={field.name}>
                        <Field.Label>{label}</Field.Label>
                        <Field.Control
                          render={<TextInput spellCheck={false} />}
                          value={
                            typeof field.value === "string" ? field.value : ""
                          }
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                          placeholder={
                            container[key] === ""
                              ? "The action's own"
                              : container[key]
                          }
                        />
                      </Field.Root>
                    )}
                  />
                ))}
              </div>
            </Card.Body>
          </Card.Root>

          <Card.Root size="compact" data-testid="environment-variables">
            <Card.Header>
              <Card.Title>Environment variables</Card.Title>
            </Card.Header>
            <Card.Body>
              <div className={styles.group}>
                {envVars.fields.length > 0 && (
                  <div
                    className={styles.envVarGrid}
                    data-testid="env-vars"
                    data-count={envVars.fields.length}
                  >
                    <span id={envNameId} className={styles.envVarHeading}>
                      Name
                    </span>
                    <span id={envValueId} className={styles.envVarHeading}>
                      Value
                    </span>
                    {envVars.fields.map((row, index) => (
                      <div
                        key={row.id}
                        className={styles.envVarRow}
                        data-testid={`env-var-${index}`}
                      >
                        <Controller
                          control={control}
                          name={`${name}.overrides.envVars.${index}.key`}
                          render={({ field }) => (
                            <TextInput
                              spellCheck={false}
                              aria-labelledby={envNameId}
                              placeholder={ENV_VAR_EXAMPLE.key}
                              value={
                                typeof field.value === "string"
                                  ? field.value
                                  : ""
                              }
                              onChange={field.onChange}
                              onBlur={field.onBlur}
                            />
                          )}
                        />
                        <Controller
                          control={control}
                          name={`${name}.overrides.envVars.${index}.value`}
                          render={({ field }) => (
                            <TextInput
                              spellCheck={false}
                              aria-labelledby={envValueId}
                              placeholder={ENV_VAR_EXAMPLE.value}
                              value={
                                typeof field.value === "string"
                                  ? field.value
                                  : ""
                              }
                              onChange={field.onChange}
                              onBlur={field.onBlur}
                            />
                          )}
                        />
                        <RobotoButton
                          variant="ghost"
                          icon={<X aria-hidden />}
                          aria-label="Remove this environment variable"
                          onClick={() => {
                            envVars.remove(index);
                          }}
                        />
                      </div>
                    ))}
                  </div>
                )}

                <RobotoButton
                  variant="secondary"
                  icon={<Plus aria-hidden size={16} />}
                  onClick={() => {
                    envVars.append({ key: "", value: "" });
                  }}
                  data-testid="add-env-var"
                >
                  Add variable
                </RobotoButton>
              </div>
            </Card.Body>
          </Card.Root>

          <p
            className={styles.hint}
            data-testid="download-inputs-note"
            data-downloads={downloadsInputs ?? undefined}
          >
            {downloadsInputs === null
              ? "Whether inputs are downloaded before the run is the action's own setting."
              : `This action ${downloadsInputs ? "downloads" : "does not download"} its inputs before the run.`}
          </p>
        </Collapsible.Panel>
      </Collapsible.Root>
    </div>
  );
}
